import { BilledToOwnerNotice } from '@components/ChooseMediaModal/BilledToOwnerNotice';
import { useTopUp } from '@lib/hooks/useTopUp';
import { OwnerShortfall } from '@lib/utils/ownerAffordability';
import {
  formatAsEstimatedMinimum,
  formatAsEstimatedRemainingOrNeeded,
  formatCredits,
  formatStorageHours,
  getRemainingOrNeededAmount,
  getResourceImpactRowLabel,
  isResourceInsufficient,
} from '@lib/utils/units';
import classNames from 'classnames';
import css from './ImpactPopup.module.scss';

export interface ImpactData {
  credits?: {
    cost: number;
    current: number;
    left: number;
  };
  storage?: {
    cost: number;
    current: number;
    left: number;
  };
}

interface ImpactPopupProps {
  data: ImpactData;
  className?: string;
  hasUnavailableInfo?: boolean; // Whether some file info is unavailable (e.g., missing duration)
  /**
   * True when the target library is billed to another account. The cost rows stay — they
   * describe the media — but every balance-relative row (current left, remaining/needed,
   * percentage, top-up) is measured against the wrong wallet and is dropped, with the notice
   * explaining the absence.
   */
  billedToAnotherAccount?: boolean;
  /** The owner's name, when known — forwarded to the notice so it can say who pays. */
  ownerName?: string;
  /**
   * Forwarded to `BilledToOwnerNotice`. This popup shares a screen with the box's own notice, so
   * an un-escalated one here would contradict the escalated one below it.
   */
  shortfall?: OwnerShortfall;
}

// Threshold for warning state: if cost exceeds this percentage of available resources, show orange warning
export const HIGH_USAGE_WARNING_THRESHOLD = 0.9;

interface PercentageResult {
  text: string;
  notEnough: boolean;
}

function formatPercentage(cost: number, total: number): PercentageResult {
  if (total <= 0) return { text: 'N/A', notEnough: true };
  const percentage = (cost / total) * 100;

  if (percentage >= 100) {
    if (percentage > 100) {
      return { text: '>100%', notEnough: true };
    }
    return { text: '100%', notEnough: true };
  }

  if (percentage < 1) {
    // For values < 1%, show the threshold it's less than
    let threshold = 1;
    let decimalPlaces = 0;
    while (percentage < threshold && decimalPlaces < 3) {
      threshold = threshold / 10;
      decimalPlaces++;
    }
    const formatted = threshold.toFixed(decimalPlaces);
    return { text: `<${formatted}%`, notEnough: false };
  }

  return { text: `≈${Math.round(percentage)}%`, notEnough: false };
}

export const ImpactPopup = ({
  data,
  className,
  hasUnavailableInfo = false,
  billedToAnotherAccount = false,
  ownerName,
  shortfall,
}: ImpactPopupProps) => {
  const { credits, storage } = data;

  // Both are cost-vs-*this*-balance comparisons, so an owner-billed upload can't be
  // "insufficient" as far as this popup knows. Keeping them false also drops the red cost
  // styling and the top-up rows that hang off them.
  const creditsInsufficient = !billedToAnotherAccount && isResourceInsufficient(credits);
  const storageInsufficient = !billedToAnotherAccount && isResourceInsufficient(storage);

  const creditsTopUp = useTopUp({
    creditCost: credits?.cost ?? 0,
    currentCredits: credits?.current ?? 0,
    insufficient: creditsInsufficient,
    source: 'upload-impact',
  });

  const storageTopUp = useTopUp({
    creditCost: 0,
    currentCredits: 0,
    insufficient: false,
    source: 'upload-impact-storage',
    // Omitted when the owner is billed: `afterProcessing` is this user's remaining storage, and
    // a plan upgrade here wouldn't affect the quota actually being consumed.
    storage: billedToAnotherAccount
      ? undefined
      : { afterProcessing: storage?.left ?? 1, duration: storage?.cost ?? 0 },
  });

  const creditsPercentage =
    credits && !creditsInsufficient && !billedToAnotherAccount
      ? formatPercentage(credits.cost, credits.current)
      : null;
  const storagePercentage =
    storage && !storageInsufficient && !billedToAnotherAccount
      ? formatPercentage(storage.cost, storage.current)
      : null;

  const creditsHighUsage =
    creditsPercentage &&
    credits &&
    credits.current > 0 &&
    credits.cost / credits.current > HIGH_USAGE_WARNING_THRESHOLD;
  const storageHighUsage =
    storagePercentage &&
    storage &&
    storage.current > 0 &&
    storage.cost / storage.current > HIGH_USAGE_WARNING_THRESHOLD;

  return (
    <div className={classNames(css.impactPopup, className)}>
      {credits && (
        <div
          className={classNames(css.section, {
            [css.sectionWithFinePrint]: hasUnavailableInfo && !storage,
          })}
        >
          <div className={css.sectionHeader}>PROCESSING IMPACT</div>
          {!billedToAnotherAccount && (
            <div className={css.row}>
              <span className={css.label}>Current credits left</span>
              {/* Use formatCredits(..., 'down') to match CreditsBox; avoid raw Math.round/Math.floor for consistent display. */}
              <span className={css.value}>{formatCredits(credits.current, 'down')}</span>
            </div>
          )}
          <div className={css.row}>
            <span className={css.label}>Credits cost</span>
            <span
              className={classNames(css.value, {
                [css.insufficientValue]: creditsInsufficient,
              })}
            >
              {formatAsEstimatedMinimum({
                formatted: formatCredits(credits.cost, 'up'),
                uncertain: hasUnavailableInfo,
              })}
            </span>
          </div>
          {!billedToAnotherAccount && (
            <div
              className={classNames(css.row, {
                [css.rowBeforeTopUp]: creditsTopUp.details && !storageInsufficient,
              })}
            >
              <span className={css.label}>
                {getResourceImpactRowLabel({
                  resource: 'credits',
                  isInsufficient: creditsInsufficient,
                })}
              </span>
              <span className={css.value}>
                {formatAsEstimatedRemainingOrNeeded({
                  formatted: formatCredits(
                    getRemainingOrNeededAmount({
                      ctx: credits,
                      isInsufficient: creditsInsufficient,
                    }),
                    creditsInsufficient ? 'up' : 'down',
                  ),
                  uncertain: hasUnavailableInfo,
                  isInsufficient: creditsInsufficient,
                })}
              </span>
            </div>
          )}
          {/* Hide credit top-up when storage is also insufficient — upgrading the plan
              addresses both, and buying credits alone won't unblock the upload. */}
          {creditsTopUp.details && !storageInsufficient && (
            <div
              className={classNames(css.topUpRow, {
                [css.topUpRowContactSales]: creditsTopUp.details.shouldContactSales,
              })}
            >
              {creditsTopUp.details.shouldContactSales && (
                <span className={css.muted}>Need more?</span>
              )}
              <a
                href="#"
                onClick={async (e) => {
                  e.preventDefault();
                  await creditsTopUp.handleTopUp();
                }}
                className={css.topUpButton}
              >
                {creditsTopUp.label}
                <span className={css.externalArrow}>↗</span>
              </a>
            </div>
          )}
          {creditsPercentage && (
            <div className={css.row}>
              <span className={css.label}>Percentage</span>
              <span
                className={classNames(css.value, {
                  [css.percentage]: !creditsHighUsage,
                  [css.percentageWarning]: creditsHighUsage,
                })}
              >
                {creditsPercentage.text}
              </span>
            </div>
          )}
        </div>
      )}
      {storage && (
        <div
          className={classNames(css.section, {
            [css.sectionWithFinePrint]: hasUnavailableInfo,
          })}
        >
          <div className={css.sectionHeader}>STORAGE IMPACT</div>
          {!billedToAnotherAccount && (
            <div className={css.row}>
              <span className={css.label}>Current storage left</span>
              <span className={css.value}>{formatStorageHours(storage.current)}</span>
            </div>
          )}
          <div className={css.row}>
            <span className={css.label}>Storage cost</span>
            <span
              className={classNames(css.value, {
                [css.insufficientValue]: storageInsufficient,
              })}
            >
              {formatAsEstimatedMinimum({
                formatted: formatStorageHours(storage.cost),
                uncertain: hasUnavailableInfo,
              })}
            </span>
          </div>
          {!billedToAnotherAccount && (
            <div
              className={classNames(css.row, {
                [css.rowBeforeTopUp]: storageTopUp.needsAction,
              })}
            >
              <span className={css.label}>
                {getResourceImpactRowLabel({
                  resource: 'storage',
                  isInsufficient: storageInsufficient,
                })}
              </span>
              <span className={css.value}>
                {formatAsEstimatedRemainingOrNeeded({
                  formatted: formatStorageHours(
                    getRemainingOrNeededAmount({
                      ctx: storage,
                      isInsufficient: storageInsufficient,
                    }),
                  ),
                  uncertain: hasUnavailableInfo,
                  isInsufficient: storageInsufficient,
                })}
              </span>
            </div>
          )}
          {storageTopUp.needsAction && storageTopUp.buttonLabel && (
            <div className={css.topUpRow}>
              <a
                href="#"
                onClick={async (e) => {
                  e.preventDefault();
                  await storageTopUp.handleTopUp();
                }}
                className={css.topUpButton}
              >
                {storageTopUp.buttonLabel}
                <span className={css.externalArrow}>↗</span>
              </a>
            </div>
          )}
          {storagePercentage && (
            <div className={css.row}>
              <span className={css.label}>Percentage</span>
              <span
                className={classNames(css.value, {
                  [css.percentage]: !storageHighUsage,
                  [css.percentageWarning]: storageHighUsage,
                })}
              >
                {storagePercentage.text}
              </span>
            </div>
          )}
        </div>
      )}
      {billedToAnotherAccount && (
        <BilledToOwnerNotice
          ownerName={ownerName}
          shortfall={shortfall}
          className={css.billedToOwnerNotice}
        />
      )}
      {hasUnavailableInfo && (
        <div className={css.finePrint}>
          We can't read the length of some media before upload, so these figures are a minimum.
        </div>
      )}
    </div>
  );
};
